import { useCallback } from 'react'
import { useGetOne, useNotify, useStore, useUpdate } from 'react-admin'
import { sanitizeGlucoseBound } from '../common'
import { mgdlToMmol, mmolToMgdl } from '../utils/glucoseUnits'
import { useSpeciesTargetRange } from './useSpeciesTargetRange'

// Per-pet RaStore keys holding the target-range local cache (canonical mmol/L).
// Scoped per pet so one patient's range doesn't bleed into another under the
// same vet. Defined HERE (not in a chart component) so every consumer — chart,
// detail panel, PDF export — layers the fallbacks the same way.
export const petTargetRangeStoreKeys = (petId?: string | number | null) => {
  const k = petId != null ? String(petId) : 'none'
  return { low: `pet.${k}.targetRangeLow`, high: `pet.${k}.targetRangeHigh` }
}

/**
 * Resolves a pet's effective glucose target range and exposes the ONE way to
 * change it.
 *
 * Precedence per bound (2026-08-10 decision: the pet RECORD is the source of
 * truth — it is what AdapetApp shows the owner and what ada-core's calorie
 * summary uses; a browser-local override must not silently diverge from it):
 *
 *   1. Pet record `target_glucose_low/high` — persisted mg/dL, converted here.
 *   2. RaStore per-pet key (mmol/L) — legacy override / offline cache.
 *   3. Species default (dog default last).
 *
 * `setTargetRange(lowMmol, highMmol)` writes BOTH stores: the local keys for
 * instant UI + offline, and a PATCH to the pet record (mg/dL) so the owner's
 * app and the backend see the same band. A failed PATCH ROLLS BACK the local
 * write and says so (P63-4): with server-wins precedence the screen keeps
 * showing the old server band on failure anyway, so keeping the failed local
 * write only left inert divergence in localStorage — and on a never-banded
 * pet it made THIS browser silently disagree with every other surface.
 *
 * Use this — NOT `useSpeciesTargetRange` — anywhere a clinical number is shown
 * to a vet (P13-3: the PDF once used the species-only hook while the panel
 * layered the override, so screen and report disagreed).
 *
 * `highMax` has no override — the range dialog only edits the target band — so
 * it stays species-derived.
 */
export function usePetTargetRange(petId?: string | number | null) {
  const {
    targetMin: speciesTargetMin,
    targetMax: speciesTargetMax,
    highMax,
    species,
    isSpeciesDefaulted,
  } = useSpeciesTargetRange(petId)

  // Same query useSpeciesTargetRange issues — react-query dedupes it.
  const { data: pet } = useGetOne('pets', { id: petId! }, { enabled: petId != null })

  const keys = petTargetRangeStoreKeys(petId)
  const [localLow, setLocalLow] = useStore<number | undefined>(keys.low)
  const [localHigh, setLocalHigh] = useStore<number | undefined>(keys.high)

  const [update] = useUpdate()
  const notify = useNotify()

  // Server fields are mg/dL; only a positive finite value counts (0/null =
  // unset, and field data can hold garbage just like the local keys can).
  const serverBoundMmol = (mgdl: unknown): number | undefined =>
    typeof mgdl === 'number' && Number.isFinite(mgdl) && mgdl > 0 ? mgdlToMmol(mgdl) : undefined

  const serverLow = serverBoundMmol(pet?.target_glucose_low)
  const serverHigh = serverBoundMmol(pet?.target_glucose_high)

  // sanitizeGlucoseBound absorbs the legacy local-key garbage ("NaN" strings,
  // nulls) that crashed the panel via .toFixed (PROD 2026-07-10, pet 68238).
  const targetMin = serverLow ?? sanitizeGlucoseBound(localLow, speciesTargetMin)
  const targetMax = serverHigh ?? sanitizeGlucoseBound(localHigh, speciesTargetMax)

  const setTargetRange = useCallback(
    (lowMmol: number, highMmol: number) => {
      // Capture the pre-edit local values so a failed PATCH can roll back —
      // `undefined` removes the key (localStorageStore.setItem contract).
      const prevLow = localLow
      const prevHigh = localHigh
      setLocalLow(lowMmol)
      setLocalHigh(highMmol)
      if (petId == null) return
      update(
        'pets',
        {
          id: petId,
          data: {
            // mmolToMgdl rounds to integer mg/dL — the record's native unit.
            target_glucose_low: mmolToMgdl(lowMmol),
            target_glucose_high: mmolToMgdl(highMmol),
          },
        },
        {
          onError: () => {
            setLocalLow(prevLow)
            setLocalHigh(prevHigh)
            notify('pos.glucose.target_range_save_failed', {
              type: 'warning',
              messageArgs: {
                _: 'Saving the target range failed — the previous range is still shown.',
              },
            })
          },
        },
      )
    },
    [petId, localLow, localHigh, setLocalLow, setLocalHigh, update, notify],
  )

  // `isSpeciesDefaulted` describes the SPECIES fallback only. A pet with an
  // explicit target_glucose_low/high is not on a species default at all, so a
  // caller disclosing "dog defaults" must not do so once the record supplies
  // the band.
  const usingSpeciesFallback = isSpeciesDefaulted && serverLow == null && serverHigh == null

  return { targetMin, targetMax, highMax, species, usingSpeciesFallback, setTargetRange }
}
